import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { LuCircleAlert } from 'react-icons/lu';
import html2pdf from 'html2pdf.js';
import { graphqlRequest } from '../../utils/graphqlRequest';
import './styles/ProfitLoss.css';

const PROFIT_LOSS_QUERY = `
  query GetFinanceProfitLoss {
    getFinanceProfitLoss {
      netIncome
      grossProfit
      netMarginPercent
      totalRevenue
      revenueBreakdown {
        label
        amount
      }
      totalCOGS
      cogsBreakdown {
        label
        amount
      }
      totalOperatingExpenses
      operatingExpensesBreakdown {
        label
        amount
      }
      operatingIncome
      otherIncomeExpenses
      otherBreakdown {
        label
        amount
      }
      period
    }
  }
`;

const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '$0';
  const abs = Math.abs(value);
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(abs);
  return value < 0 ? `(${formatted})` : formatted;
};

export default function ProfitLoss() {
  const reportRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProfitLoss = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await graphqlRequest(PROFIT_LOSS_QUERY);
      setData(result.getFinanceProfitLoss);
    } catch (err) {
      console.error('Failed to fetch profit & loss data:', err);
      setError('Failed to load profit & loss data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfitLoss();
  }, []);

  const handleDownloadPdf = async () => {
    const element = reportRef.current;
    if (!element) return;

    try {
      await html2pdf()
        .set({
          margin: [0.3, 0.3, 0.3, 0.3],
          filename: `profit-loss-${(data?.period || 'report').replace(/\s+/g, '-').toLowerCase()}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, scrollY: 0 },
          jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
        })
        .from(element)
        .save();
    } catch (err) {
      console.error('Failed to download profit & loss PDF:', err);
      toast.error('Failed to download PDF. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="pl-page">
        <div className="pl-loading">
          <div className="spinner" />
          <p>Loading profit & loss data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pl-page">
        <div className="pl-error">
          <LuCircleAlert size={40} />
          <p>{error}</p>
          <button onClick={fetchProfitLoss}>Retry</button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="pl-page" ref={reportRef}>
      {/* HEADER */}
      <div className="pl-header">
        <div>
          <h1>Profit & Loss Statement</h1>
          <p>Executive financial summary</p>
        </div>

        <div className="pl-actions">
          <button type="button" className="outline-btn">Show Comparison</button>
          <button type="button" className="primary-btn" onClick={handleDownloadPdf}>
            Download PDF
          </button>
        </div>
      </div>

      {/* PERIOD */}
      <div className="period-card">
        <label>Period</label>
        <span className="period-value">{data.period}</span>
      </div>

      {/* KPI CARDS */}
      <div className="pl-kpis">
        <div className="kpi green">
          <span>Net Income</span>
          <h2>{formatCurrency(data.netIncome)}</h2>
        </div>

        <div className="kpi blue">
          <span>Gross Profit</span>
          <h2>{formatCurrency(data.grossProfit)}</h2>
        </div>

        <div className="kpi purple">
          <span>Net Margin</span>
          <h2>{data.netMarginPercent.toFixed(1)}%</h2>
        </div>
      </div>

      {/* DETAILED STATEMENT */}
      <div className="statement-card">
        <h3>Detailed Statement</h3>

        {/* Revenue */}
        <div className="group">
          <div className="row bold">
            <span>Revenue</span>
            <span>{formatCurrency(data.totalRevenue)}</span>
          </div>
          {data.revenueBreakdown.map((item, i) => (
            <div className="row sub" key={`rev-${i}`}>
              <span>{item.label}</span>
              <span>{formatCurrency(item.amount)}</span>
            </div>
          ))}
          {data.revenueBreakdown.length === 0 && (
            <div className="row sub muted">
              <span>No revenue data available</span>
              <span>$0</span>
            </div>
          )}
        </div>

        {/* COGS */}
        <div className="group">
          <div className="row bold red">
            <span>Cost of Goods Sold</span>
            <span>({formatCurrency(data.totalCOGS)})</span>
          </div>
          {data.cogsBreakdown.map((item, i) => (
            <div className="row sub red" key={`cogs-${i}`}>
              <span>{item.label}</span>
              <span>({formatCurrency(item.amount)})</span>
            </div>
          ))}
          {data.cogsBreakdown.length === 0 && (
            <div className="row sub muted">
              <span>No cost data available</span>
              <span>$0</span>
            </div>
          )}
        </div>

        <div className="highlight blue">
          <span>Gross Profit</span>
          <span>{formatCurrency(data.grossProfit)}</span>
        </div>

        {/* Operating Expenses */}
        {data.operatingExpensesBreakdown.length > 0 && (
          <div className="group">
            <div className="row bold red">
              <span>Operating Expenses</span>
              <span>({formatCurrency(data.totalOperatingExpenses)})</span>
            </div>
            {data.operatingExpensesBreakdown.map((item, i) => (
              <div className="row sub red" key={`opex-${i}`}>
                <span>{item.label}</span>
                <span>({formatCurrency(item.amount)})</span>
              </div>
            ))}
          </div>
        )}

        <div className="highlight blue">
          <span>Operating Income</span>
          <span>{formatCurrency(data.operatingIncome)}</span>
        </div>

        {/* Other Income/Expenses */}
        {data.otherBreakdown.length > 0 && (
          <div className="group">
            <div className="row bold red">
              <span>Other Income / Expenses</span>
              <span>({formatCurrency(data.otherIncomeExpenses)})</span>
            </div>
            {data.otherBreakdown.map((item, i) => (
              <div
                className={`row sub ${item.amount < 0 ? 'red' : ''}`}
                key={`other-${i}`}
              >
                <span>{item.label}</span>
                <span>{formatCurrency(item.amount)}</span>
              </div>
            ))}
          </div>
        )}

        <div className="highlight green">
          <span>Net Income</span>
          <span>{formatCurrency(data.netIncome)}</span>
        </div>
      </div>
    </div>
  );
}
